<template>
  <!-- SEÇÃO: PROGRAMAÇÃO -->
  <section class="programming-content mt-10 mb-10 px-4 lg:px-4 xl:px-0" id="programacao">
    <div class="programming-area max-w-screen-xl m-auto">

      <!-- Título da Seção -->
      <div class="mb-8">
        <div class="flex items-center gap-2 relative pb-3">
          <div>
            <h2 class="text-lg md:text-xl font-medium tracking-wide"
              :style="{ color: customization?.section_title_color || '#1c243b' }">
              {{ customization?.schedule_title || 'Programação' }}
            </h2>
            <h2 class="text-xs md:text-sm font-normal tracking-wide"
              :style="{ color: customization?.section_subtitle_color || '#666666' }">
              {{ customization?.schedule_subtitle || 'Confira abaixo o que preparamos para esse dia especial' }}
            </h2>
          </div>
          <div class="absolute bottom-0 left-0 w-full h-px bg-gray-200"></div>
          <div class="absolute bottom-0 left-0 h-px w-full max-w-[480px]"
            :style="{ backgroundColor: customization?.section_divider_color || '#e5e7eb' }"></div>
        </div>
      </div>

      <!-- FILTROS DE DIA -->
      <div v-if="programmingDays.length > 1" class="flex flex-col sm:flex-row gap-3 mb-8">
        <button
          v-for="(day, index) in programmingDays"
          :key="index"
          @click="selectDay(index)"
          @mouseenter="hoveredDay = index"
          @mouseleave="hoveredDay = null"
          class="flex-1 py-3 px-4 rounded-lg transition-all duration-300 text-center flex flex-col justify-center min-h-[60px]"
          :style="dayButtonStyle(index)"
        >
          <div class="font-semibold text-sm md:text-md mb-1" :style="{ color: dayTextColor(index) }">
            {{ day.label }}
          </div>
          <div class="text-xs md:text-sm" :style="{ color: dayTextColor(index) }">
            {{ day.date }}
          </div>
        </button>
      </div>

      <!-- CARDS DE PROGRAMAÇÃO -->
      <div v-if="filteredProgramming && filteredProgramming.length > 0" class="space-y-4">
        <div
          v-for="(item, index) in filteredProgramming"
          :key="index"
          class="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-sm transition-shadow duration-300"
        >
          <div class="mb-4">
            <!-- HORÁRIO + ÍCONE -->
            <div class="flex items-center gap-2 mb-3">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                :style="{ color: fontColor }">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <span class="text-sm font-semibold" :style="{ color: fontColor }">
                {{ formatTime(item.start_time, item.end_time) }}
              </span>
            </div>

            <!-- TÍTULO DA ATIVIDADE -->
            <h3 class="text-base font-semibold mb-2" :style="{ color: fontColor }">
              {{ item.title }}
            </h3>

            <!-- DESCRIÇÃO (mantida legível) -->
            <p class="text-sm text-gray-600 leading-relaxed">
              {{ item.description }}
            </p>
          </div>

          <!-- PALESTRANTE -->
          <div v-if="item.speakers && item.speakers.length > 0" class="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
            <div v-for="speaker in item.speakers" :key="speaker.id" class="flex items-center gap-3">
              <img
                :src="speaker.photo || '/images/default-speaker.jpg'"
                :alt="speaker.name"
                class="w-12 h-12 rounded-md object-cover ring-2 ring-white shadow-sm"
                loading="lazy"
              />
              <div>
                <div class="text-sm font-medium" :style="{ color: fontColor }">
                  {{ speaker.name }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ speaker.role || 'Palestrante' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-12">
        <p class="text-gray-500">Nenhuma programação cadastrada ainda.</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useLandingPage } from '@/composables/useLandingPage'

const { schedules, customization } = useLandingPage()

const selectedDay = ref(0)
const hoveredDay = ref(null)

// Cor principal do texto (usada em horário, título, etc.)
const fontColor = computed(() => customization.value?.font_color || '#1f2937')

const programmingDays = computed(() => {
  if (!schedules.value || schedules.value.length === 0) return []
  const uniqueDates = [...new Set(schedules.value.map(s => s.date))]
  return uniqueDates.map((date, index) => {
    const dateObj = new Date(date + 'T00:00:00')
    const formattedDate = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })
    return {
      label: `Dia ${String(index + 1).padStart(2, '0')}`,
      date: formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1),
      rawDate: date
    }
  })
})

const filteredProgramming = computed(() => {
  if (!schedules.value || programmingDays.value.length === 0) return []
  const selectedDate = programmingDays.value[selectedDay.value]?.rawDate
  return schedules.value.filter(item => item.date === selectedDate)
})

const selectDay = (index) => {
  selectedDay.value = index
}

// Estilo do botão de filtro de dia
const dayButtonStyle = (index) => {
  const isSelected = selectedDay.value === index
  const isHovered = hoveredDay.value === index && !isSelected
  const baseColor = customization.value?.button_color || '#2563eb'
  const hoverColor = customization.value?.button_hover_color || baseColor

  return {
    backgroundColor: isSelected ? baseColor : (isHovered ? hoverColor : 'white'),
    transition: 'background-color 0.3s ease',
    border: isSelected || isHovered ? 'none' : '1px solid #e5e7eb',
    boxShadow: isSelected ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
  }
}

// Cor do texto nos botões de dia (branco quando selecionado ou hover)
const dayTextColor = (index) => {
  const isActive = selectedDay.value === index || hoveredDay.value === index
  return isActive ? 'white' : fontColor.value
}

const formatTime = (startTime, endTime) => {
  if (!startTime) return ''
  const formatHour = (time) => time ? time.slice(0, 5) : ''
  const start = formatHour(startTime)
  const end = formatHour(endTime)
  return end ? `${start} - ${end}` : start
}
</script>